import {ActivatedRoute, Router} from "@angular/router";
import {Component, OnInit} from "@angular/core";
import {
  ClientContact,
  ClientITProfile,
  NetworkDescriptionAndDiagram,
  SystemInterconnection,
  SystemRolePrivilege,
  User
} from "../../_models";
import {FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {
  AlertService,
  AuthenticationService,
  ClientProfileService,
  FileBlobService,
  ProjectService,
  TenantService
} from "../../_services";
import {SarProfileService} from "../../_services/sarProfile.service";
import {Access, ClientContactRole, CompanyType, DataDirection, LoginHelper, Role} from "../../helpers";
import {NameAndIdentifier} from "../../_models/nameAndIdentifier";
import {SystemLocation} from "../../_models/systemLocation";
import {Device} from "../../_models/device";
import {RoleSensitivityLevel} from "../../_models/roleSensitivityLevel";
import {OtherDesignatedContact} from "../../_models/otherDesignatedContact";
import {Constants} from "../../helpers/constants";

@Component({
    standalone: false,
  selector: "sar-profile",
  templateUrl: 'sar.profile.component.html',
  styleUrls: ['./sar.profile.component.scss'],
})

export class SarProfileComponent implements OnInit {
  pageTitle;
  loading = false;
  currentUser: User;
  sarProfileId: number;
  formGroup: FormGroup;
  lowMediumHigh = {0: 'Low (L)', 1: 'Moderate (M)', 2: 'High (H)'};
  profiles: any[] = [];
  profile: any;
  window = window;
  token: string;
  api: any;
  projects: any[];
  itProfiles: any[];
  projectItProfiles: any[];

  rowGroupMetadata = {};

  executiveSummaryCols: any[] = [
    {field: 'identifier', header: 'FedRAMP Unique Identifier', width: '20%'},
    {field: 'csp', header: 'CSP', width: '50%'},
    {field: 'cso', header: 'CSO', width: '30%'},
    {field: 'serviceModel', header: 'Service Model', width: '30%'},
    {field: 'deploymentModel', header: 'Deployment Model', width: '30%'},
    {field: 'impactLevel', header: 'Impact Level', width: '30%'},
  ];
  executiveSummaryFormGroup: FormGroup;
  executiveSummaryDialogVisible = false;
  editedExecutiveSummary: any;

  riskCols: any[] = [
    {field: 'category', header: 'Category', width: '20%'},
    {field: 'total', header: 'Total', width: '50%'},
    {field: 'operationalRequirement', header: 'Operational Requirement', width: '30%'},
    {field: 'vendorDependency', header: 'Vendor Dependency', width: '30%'},
  ];
  riskFormGroup: FormGroup;
  riskDialogVisible = false;
  editedRisk: any;

  siteNameAndAddressCols: any[] = [
    {field: 'dataCenterSiteName', header: 'Date Center Site Name', width: '20%'},
    {field: 'address', header: 'Address', width: '50%'},
    {field: 'description', header: 'Description of Components', width: '30%'},
  ];
  siteNameAndAddressFormGroup: FormGroup;
  siteNameAndAddressDialogVisible = false;
  editedSiteNameAndAddress: any;

  deviationCols: any[] = [
    {field: 'id', header: 'Deviation ID', width: '20%'},
    {field: 'description', header: 'Description of Components', width: '30%'},
    {field: 'justification', header: 'Justification', width: '50%'},
  ];
  deviationFormGroup: FormGroup;
  deviationDialogVisible = false;
  editedDeviation: any;

  zipFileCols: any[] = [
    {field: 'title', header: 'Title', width: '50%'},
    {field: 'fileName', header: 'File Name (Include Extension)', width: '50%'},
  ];
  zipFileFormGroup: FormGroup;
  zipFileDialogVisible = false;
  editedZipFile: any;
  editedZipFileList: any[];
  zipFiles = {};

  scanSummaryCols: any[] = [
    {field: 'identifier', header: 'Identifier', width: '20%'},
    {field: 'componentDescription', header: 'Product/Embedded Component Description', width: '50%'},
    {field: 'rationale', header: 'Rationale', width: '30%'},
    {field: 'metodologyDescription', header: 'Alternative Assessment Methodology Description', width: '30%'},
  ];
  scanSummaryFormGroup: FormGroup;
  scanSummaryDialogVisible = false;
  editedScanSummary: any;

  auxiliaryDocumentCols: any[] = [
    {field: 'title', header: 'Title', width: '20%'},
    {field: 'version', header: 'Version', width: '50%'},
    {field: 'fileName', header: 'Filename', width: '30%'},
  ];
  auxiliaryDocumentFormGroup: FormGroup;
  auxiliaryDocumentDialogVisible = false;
  editedAuxiliaryDocument: any;


  constructor(
    public router: Router,
    public route: ActivatedRoute,
    public authenticationService: AuthenticationService,
    public projectService: ProjectService,
    public clientProfileService: ClientProfileService,
    public _formBuilder: FormBuilder,
    public sarProfileService: SarProfileService,
    public fileBlobService: FileBlobService,
    public tenantService: TenantService,
    public alertService: AlertService) {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.api = window.location.origin || Constants.DefaultApiHost;
    this.token = LoginHelper.getFileToken();
    this.clientProfileService.getPublicSettings(LoginHelper.getTenantName()).subscribe(s => this.pageTitle = s.menuLabels['SAR'] || 'SAR Profiles');
  }

  ngOnInit() {
    const tenantName = LoginHelper.getTenantName();
    if (tenantName == null || LoginHelper.getUserToken() == null) {
      this.authenticationService.logout();

      return;
    }

    this.clientProfileService.getClientProfiles(tenantName).subscribe(s => {
      this.itProfiles = s;
    });

    this.currentUser = JSON.parse(sessionStorage.getItem('currentUser'));

    this.formGroup = this._formBuilder.group({
      sarProfileId: [0],
      name: ['', Validators.required],
      date: [new Date()],
      version: [''],
      csoName: [''],
      preparedByName: [''],
      preparedByAddress: [''],
      preparedByBuilding: [''],
      preparedByCity: [''],
      preparedForName: [''],
      preparedForAddress: [''],
      preparedForBuilding: [''],
      preparedForCity: [''],
      relatedSapVersion: [''],
      relatedSapVersionDate: [''],
      referencedSapVersion: [''],
      referencedSapVersionDate: [''],
      iaName: [''],
      cspName: [''],
      assessmentType: [0],
      isSignificantChangeDocumentationAndSsp: [false],
      datesType: [0],
      initialStart: [new Date()],
      initialEnd: [new Date()],
      remediationStart: [new Date()],
      remediationEnd: [new Date()],
      assessmentStart: [new Date()],
      assessmentEnd: [new Date()],
      baselineInitial: [0],
      baselineAnnual: [0],
      isForAlsoIncludes: [0],
      changeChanges: [0],
      aoJab: [0],
      relatedActivities: [0],
      includingThoseApplicable: [false],
      sapVersion: [''],
      sapVersionDate: [new Date()],
      testingTypesManual: [false],
      testingTypesPenetration: [false],
      testingTypesWeb: [false],
      testingTypesDatabase: [false],
      testingTypesOperationSystem: [false],
      testingTypesContainer: [false],
      recommendsName: [''],
      notRecommendsName: [''],
      recommendsContinued: [''],
      notRecommendsContinued: [''],
      recommendsAssessment: [''],
      isRecommendsAssessmentMultiple: [false],
      approvedSignificantChanges: [''],
      notRecommendsAssessment: [''],
      isNotRecommendsAssessmentMultiple: [false],
      notApprovedSignificantChanges: [''],
//      retTableId: [0],
//      srtmWorkbookId: [0],
      infrastructureScannerName: [''],
      infrastructureScannerVendor: [''],
      infrastructureScannerVersion: [''],
      infrastructurePercentageScanned: [0],
      isInfrastructureDiscrepancy: [false],
      infrastructureDiscrepancyReasons: [''],
      infrastructureScanDate: [new Date()],
      dbScannerName: [''],
      dbScannerVendor: [''],
      dbScannerVersion: [''],
      dbPercentageScanned: [0],
      isDbScan: [false],
      isDbDiscrepancy: [false],
      dbDiscrepancyReasons: [''],
      dbScanDate: [new Date()],
      webScannerName: [''],
      webScannerVendor: [''],
      webScannerVersion: [''],
      webPercentageAppScanned: [0],
      webPercentageApiScanned: [0],
      isWebScan: [false],
      isWebDiscrepancy: [false],
      webDiscrepancyReasons: [''],
      webScanDate: [new Date()],
      containerScannerName: [''],
      containerScannerVendor: [''],
      containerScannerVersion: [''],
      containerPercentageScanned: [0],
      isContainerScan: [false],
      isContainerDiscrepancy: [false],
      containerDiscrepancyReasons: [''],
      containerScanDate: [new Date()],
      otherScanDate: [new Date()],
      penTestLocation: [''],
      penTestDate: [new Date()],
      projectId: [null],
      clientItProfileId: [null],
      //penTestId: [0],
    });

    this.executiveSummaryFormGroup = this._formBuilder.group({
      identifier: ['', Validators.required],
      csp: ['', Validators.required],
      cso: ['', Validators.required],
      serviceModel: ['', Validators.required],
      deploymentModel: ['', Validators.required],
      impactLevel: ['', Validators.required],
    });

    this.riskFormGroup = this._formBuilder.group({
      category: ['', Validators.required],
      total: ['', Validators.required],
      operationalRequirement: ['', Validators.required],
      vendorDependency: ['', Validators.required],
    });

    this.siteNameAndAddressFormGroup = this._formBuilder.group({
      dataCenterSiteName: ['', Validators.required],
      address: ['', Validators.required],
      description: ['', Validators.required],
    });

    this.deviationFormGroup = this._formBuilder.group({
      id: ['', Validators.required],
      description: ['', Validators.required],
      justification: ['', Validators.required],
    });

    this.zipFileFormGroup = this._formBuilder.group({
      title: ['', Validators.required],
      fileName: ['', Validators.required],
      kind: [0]
    });

    this.scanSummaryFormGroup = this._formBuilder.group({
      identifier: ['', Validators.required],
      componentDescription: ['', Validators.required],
      rationale: ['', Validators.required],
      metodologyDescription: ['', Validators.required],
    });

    this.auxiliaryDocumentFormGroup = this._formBuilder.group({
      title: ['', Validators.required],
      version: ['', Validators.required],
      fileName: ['', Validators.required],
    });

    this.route.params.subscribe(p => {
      this.sarProfileId = +p['id'];
      this.loadProfiles();
    });
  }

  loadProfiles() {
    this.sarProfileService.getSarProfiles()
      .subscribe((profiles: any[]) => {

        if (profiles == null) {
          return;
        }

        this.profiles = profiles;
        this.profile = profiles.find(p => p.sarProfileId == this.sarProfileId);
        if (!this.profile && this.profiles.length) {
          this.router.navigate(['/profiles', 'sarProfile', this.profiles[0].sarProfileId]);
        } else {
          this.selectProfile(this.profile);
        }
      });
  }

  onProfileChanged(event) {
    this.router.navigate(['/profiles/sarProfile', event.value]);
  }

  createProfile() {
    this.sarProfileService
      .saveSarProfile({name: 'SAR Profile'})
      .subscribe((r) => this.router.navigate(['/profiles/sarProfile', r.sarProfileId]));
  }

  selectProfile(profile) {
    this.projectService.getAllShort().subscribe(p => {
      this.projects = p;
      this.selectProject(profile.projectId || p[0].projectId);

    });

    if (!profile) {
      profile = {
        sarProfileId: 0,
        name: '',
        version: '',
        date: new Date(),
        csoName: '',
        preparedByName: '',
        preparedByAddress: '',
        preparedByBuilding: '',
        preparedByCity: '',
        preparedForName: '',
        preparedForAddress: '',
        preparedForBuilding: '',
        preparedForCity: '',
        relatedSapVersion: '',
        relatedSapVersionDate: '',
        referencedSapVersion: '',
        referencedSapVersionDate: '',
        iaName: '',
        cspName: '',
        assessmentType: 0,
        isSignificantChangeDocumentationAndSsp: false,
        datesType: 0,
        initialStart: new Date(),
        initialEnd: new Date(),
        remediationStart: new Date(),
        remediationEnd: new Date(),
        assessmentStart: new Date(),
        assessmentEnd: new Date(),
        baselineInitial: 0,
        baselineAnnual: 0,
        isForAlsoIncludes: 0,
        changeChanges: 0,
        aoJab: 0,
        relatedActivities: 0,
        includingThoseApplicable: false,
        sapVersion: '',
        sapVersionDate: new Date(),
        testingTypesManual: false,
        testingTypesPenetration: false,
        testingTypesWeb: false,
        testingTypesDatabase: false,
        testingTypesOperationSystem: false,
        testingTypesContainer: false,
        recommendsName: '',
        notRecommendsName: '',
        recommendsContinued: '',
        notRecommendsContinued: '',
        recommendsAssessment: '',
        isRecommendsAssessmentMultiple: false,
        approvedSignificantChanges: '',
        notRecommendsAssessment: '',
        isNotRecommendsAssessmentMultiple: false,
        notApprovedSignificantChanges: '',
//      retTableId: 0,
//      srtmWorkbookId: 0,
        infrastructureScannerName: '',
        infrastructureScannerVendor: '',
        infrastructureScannerVersion: '',
        infrastructurePercentageScanned: 0,
        isInfrastructureDiscrepancy: false,
        infrastructureDiscrepancyReasons: '',
        infrastructureScanDate: new Date(),
        dbScannerName: '',
        dbScannerVendor: '',
        dbScannerVersion: '',
        dbPercentageScanned: 0,
        isDbScan: false,
        isDbDiscrepancy: false,
        dbDiscrepancyReasons: '',
        dbScanDate: new Date(),
        webScannerName: '',
        webScannerVendor: '',
        webScannerVersion: '',
        webPercentageAppScanned: 0,
        webPercentageApiScanned: 0,
        isWebScan: false,
        isWebDiscrepancy: false,
        webDiscrepancyReasons: '',
        webScanDate: new Date(),
        containerScannerName: '',
        containerScannerVendor: '',
        containerScannerVersion: '',
        containerPercentageScanned: 0,
        isContainerScan: false,
        isContainerDiscrepancy: false,
        containerDiscrepancyReasons: '',
        containerScanDate: new Date(),
        otherScanDate: new Date(),
        penTestLocation: '',
        penTestDate: new Date(),
        projectId: null,
        clientItProfileId: null,
        //penTestId: 0,
      };
    }

    this.profile = profile;

    profile.sarZipFiles = profile.sarZipFiles || [];
    this.zipFiles['Infrastructure'] = profile.sarZipFiles.filter(s => s.kind === 0);
    this.zipFiles['Db'] = profile.sarZipFiles.filter(s => s.kind === 1);
    this.zipFiles['Web'] = profile.sarZipFiles.filter(s => s.kind === 2);
    this.zipFiles['Container'] = profile.sarZipFiles.filter(s => s.kind === 3);
    this.zipFiles['Other'] = profile.sarZipFiles.filter(s => s.kind === 4);

    this.formGroup.patchValue({
      sarProfileId: this.profile.sarProfileId,
      name: this.profile.name,
      version: this.profile.name,
      date: this.profile.date,
      csoName: this.profile.csoName,
      preparedByName: this.profile.preparedByName,
      preparedByAddress: this.profile.preparedByAddress,
      preparedByBuilding: this.profile.preparedByBuilding,
      preparedByCity: this.profile.preparedByCity,
      preparedForName: this.profile.preparedForName,
      preparedForAddress: this.profile.preparedForAddress,
      preparedForBuilding: this.profile.preparedForBuilding,
      preparedForCity: this.profile.preparedForCity,
      relatedSapVersion: this.profile.relatedSapVersion,
      relatedSapVersionDate: this.profile.relatedSapVersionDate,
      referencedSapVersion: this.profile.referencedSapVersion,
      referencedSapVersionDate: this.profile.referencedSapVersionDate,
      iaName: this.profile.iaName,
      cspName: this.profile.cspName,
      assessmentType: this.profile.assessmentType,
      isSignificantChangeDocumentationAndSsp: this.profile.isSignificantChangeDocumentationAndSsp,
      datesType: this.profile.datesType,
      initialStart: this.profile.initialStart,
      initialEnd: this.profile.initialEnd,
      remediationStart: this.profile.remediationStart,
      remediationEnd: this.profile.remediationEnd,
      assessmentStart: this.profile.assessmentStart,
      assessmentEnd: this.profile.assessmentEnd,
      baselineInitial: this.profile.baselineInitial,
      baselineAnnual: this.profile.baselineAnnual,
      isForAlsoIncludes: this.profile.isForAlsoIncludes,
      changeChanges: this.profile.changeChanges,
      aoJab: this.profile.aoJab,
      relatedActivities: this.profile.relatedActivities,
      includingThoseApplicable: this.profile.includingThoseApplicable,
      sapVersion: this.profile.sapVersion,
      sapVersionDate: this.profile.sapVersionDate,
      testingTypesManual: (this.profile.testingTypes & 1) !== 0,
      testingTypesPenetration: (this.profile.testingTypes & 1) !== 0,
      testingTypesWeb: (this.profile.testingTypes & 1) !== 0,
      testingTypesDatabase: (this.profile.testingTypes & 1) !== 0,
      testingTypesOperationSystem: (this.profile.testingTypes & 1) !== 0,
      testingTypesContainer: (this.profile.testingTypes & 1) !== 0,
      recommendsName: this.profile.recommendsName,
      notRecommendsName: this.profile.notRecommendsName,
      recommendsContinued: this.profile.recommendsContinued,
      notRecommendsContinued: this.profile.notRecommendsContinued,
      recommendsAssessment: this.profile.recommendsAssessment,
      isRecommendsAssessmentMultiple: this.profile.isRecommendsAssessmentMultiple,
      approvedSignificantChanges: this.profile.approvedSignificantChanges,
      notRecommendsAssessment: this.profile.notRecommendsAssessment,
      isNotRecommendsAssessmentMultiple: this.profile.isNotRecommendsAssessmentMultiple,
      notApprovedSignificantChanges: this.profile.notApprovedSignificantChanges,
//      retTableId: retTableId,
//      srtmWorkbookId: srtmWorkbookId,
      infrastructureScannerName: this.profile.infrastructureScannerName,
      infrastructureScannerVendor: this.profile.infrastructureScannerVendor,
      infrastructureScannerVersion: this.profile.infrastructureScannerVersion,
      infrastructurePercentageScanned: this.profile.infrastructurePercentageScanned,
      isInfrastructureDiscrepancy: this.profile.isInfrastructureDiscrepancy,
      infrastructureDiscrepancyReasons: this.profile.infrastructureDiscrepancyReasons,
      infrastructureScanDate: this.profile.infrastructureScanDate,
      dbScannerName: this.profile.dbScannerName,
      dbScannerVendor: this.profile.dbScannerVendor,
      dbScannerVersion: this.profile.dbScannerVersion,
      dbPercentageScanned: this.profile.dbPercentageScanned,
      isDbScan: this.profile.isDbScan,
      isDbDiscrepancy: this.profile.isDbDiscrepancy,
      dbDiscrepancyReasons: this.profile.dbDiscrepancyReasons,
      dbScanDate: this.profile.dbScanDate,
      webScannerName: this.profile.webScannerName,
      webScannerVendor: this.profile.webScannerVendor,
      webScannerVersion: this.profile.webScannerVersion,
      webPercentageAppScanned: this.profile.webPercentageAppScanned,
      webPercentageApiScanned: this.profile.webPercentageApiScanned,
      isWebScan: this.profile.isWebScan,
      isWebDiscrepancy: this.profile.isWebDiscrepancy,
      webDiscrepancyReasons: this.profile.webDiscrepancyReasons,
      webScanDate: this.profile.webScanDate,
      containerScannerName: this.profile.containerScannerName,
      containerScannerVendor: this.profile.containerScannerVendor,
      containerScannerVersion: this.profile.containerScannerVersion,
      containerPercentageScanned: this.profile.containerPercentageScanned,
      isContainerScan: this.profile.isContainerScan,
      isContainerDiscrepancy: this.profile.isContainerDiscrepancy,
      containerDiscrepancyReasons: this.profile.containerDiscrepancyReasons,
      containerScanDate: this.profile.containerScanDate,
      otherScanDate: this.profile.otherScanDate,
      penTestLocation: this.profile.penTestLocation,
      penTestDate: this.profile.penTestDate,
      projectId: this.profile.projectId,
      clientItProfileId: this.profile.clientItProfileId,
      //penTestId: 0,
    });
  }

  selectProject(projectId) {
    this.projectItProfiles = this.projects.find(p => p.projectId == projectId).clientITProfileIds.split(';').map(i => this.itProfiles.find(p => p.clientITProfileId == i)).filter(p => p);
  }

  projectChanged(e) {
    this.selectProject(e.value);
  }

  onDone() {
    if (!this.formGroup.valid) return;
    this.alertService.clear();
    const value = {
      ...this.profile, ...this.formGroup.value,
      sarZipFiles: [...this.zipFiles['Infrastructure'] || [], ...this.zipFiles['Db'] || [], ...this.zipFiles['Web'] || [], ...this.zipFiles['Container'] || [], ...this.zipFiles['Other'] || []],
    };
    value.testingTypes = (this.formGroup.value.testingTypesManual ? 1 : 0) +
      (this.formGroup.value.testingTypesPenetration ? 1 : 0) +
      (this.formGroup.value.testingTypesWeb ? 1 : 0) +
      (this.formGroup.value.testingTypesDatabase ? 1 : 0) +
      (this.formGroup.value.testingTypesOperationSystem ? 1 : 0) +
      (this.formGroup.value.testingTypesContainer ? 1 : 0);

    this.sarProfileService.saveSarProfile(value)
      .subscribe(() => {
          this.alertService.success("Profile Successfully Saved");
          this.loadProfiles();
        },
        error => {
          const errorMessage = 'An error occurred while updating client SAR profile.';
          this.alertService.error(errorMessage);
        }
      );
  }

  removeProfile() {
    this.sarProfileService.deleteSarProfile(this.profile.sarProfileId).subscribe(() => {
      this.router.navigate(['/profiles/sarProfile']);
    });
  }


  showExecutiveSummaryDialog(item = <any>{}) {
    this.editedExecutiveSummary = item;
    this.executiveSummaryFormGroup.setValue({
      identifier: item.identifier || '',
      csp: item.csp || '',
      cso: item.cso || '',
      serviceModel: item.serviceModel || '',
      deploymentModel: item.deploymentModel || '',
      impactLevel: item.impactLevel || '',
    });
    this.executiveSummaryDialogVisible = true;
  }

  saveExecutiveSummary() {
    if (!this.executiveSummaryFormGroup.valid) {
      Object.keys(this.executiveSummaryFormGroup.controls).forEach(field => {
        const control = this.executiveSummaryFormGroup.get(field);
        control.markAsTouched({onlySelf: true});
      });
      return;
    }
    const data = this.executiveSummaryFormGroup.value;
    const pos = this.profile.sarExecutiveSummaries.indexOf(this.editedExecutiveSummary);
    if (pos >= 0) {
      this.profile.sarExecutiveSummaries[pos] = {...this.editedExecutiveSummary, ...data};
    } else {
      this.profile.sarExecutiveSummaries.push({...data});
    }
    this.executiveSummaryDialogVisible = false;
    this.editedExecutiveSummary = null;
  }

  deleteExecutiveSummary(item) {
    this.profile.sarExecutiveSummaries = this.profile.sarExecutiveSummaries.filter(s => s !== item);
  }


  showRiskDialog(item = <any>{}) {
    this.editedRisk = item;
    this.riskFormGroup.setValue({
      category: item.category || '',
      total: item.total || '',
      operationalRequirement: item.operationalRequirement || '',
      vendorDependency: item.vendorDependency || ''
    });
    this.riskDialogVisible = true;
  }

  saveRisk() {
    if (!this.riskFormGroup.valid) {
      Object.keys(this.riskFormGroup.controls).forEach(field => {
        const control = this.riskFormGroup.get(field);
        control.markAsTouched({onlySelf: true});
      });
      return;
    }
    const data = this.riskFormGroup.value;
    const pos = this.profile.sarRisks.indexOf(this.editedRisk);
    if (pos >= 0) {
      this.profile.sarRisks[pos] = {...this.editedRisk, ...data};
    } else {
      this.profile.sarRisks.push({...data});
    }
    this.riskDialogVisible = false;
    this.editedRisk = null;
  }

  deleteRisk(item) {
    this.profile.sarRisks = this.profile.sarRisks.filter(s => s !== item);
  }


  showSiteNameAndAddressDialog(item = <any>{}) {
    this.editedSiteNameAndAddress = item;
    this.siteNameAndAddressFormGroup.setValue({
      dataCenterSiteName: item.name || '',
      address: item.address || '',
      description: item.description || ''
    });
    this.siteNameAndAddressDialogVisible = true;
  }

  saveSiteNameAndAddress() {
    if (!this.siteNameAndAddressFormGroup.valid) {
      Object.keys(this.siteNameAndAddressFormGroup.controls).forEach(field => {
        const control = this.siteNameAndAddressFormGroup.get(field);
        control.markAsTouched({onlySelf: true});
      });
      return;
    }
    const data = this.siteNameAndAddressFormGroup.value;
    const pos = this.profile.sarSiteNameAndAddresses.indexOf(this.editedSiteNameAndAddress);
    if (pos >= 0) {
      this.profile.sarSiteNameAndAddresses[pos] = {...this.editedSiteNameAndAddress, ...data};
    } else {
      this.profile.sarSiteNameAndAddresses.push({...data});
    }
    this.siteNameAndAddressDialogVisible = false;
    this.editedSiteNameAndAddress = null;
  }

  deleteSiteNameAndAddress(item) {
    this.profile.sarSiteNameAndAddresses = this.profile.sarSiteNameAndAddresses.filter(s => s !== item);
  }


  showDeviationDialog(item = <any>{}) {
    this.editedDeviation = item;
    this.deviationFormGroup.setValue({
      id: item.name || '',
      description: item.description || '',
      justification: item.address || '',
    });
    this.deviationDialogVisible = true;
  }

  saveDeviation() {
    if (!this.deviationFormGroup.valid) {
      Object.keys(this.deviationFormGroup.controls).forEach(field => {
        const control = this.deviationFormGroup.get(field);
        control.markAsTouched({onlySelf: true});
      });
      return;
    }
    const data = this.deviationFormGroup.value;
    const pos = this.profile.sarDeviations.indexOf(this.editedDeviation);
    if (pos >= 0) {
      this.profile.sarDeviations[pos] = {...this.editedDeviation, ...data};
    } else {
      this.profile.sarDeviations.push({...data});
    }
    this.deviationDialogVisible = false;
    this.editedDeviation = null;
  }

  deleteDeviation(item) {
    this.profile.sarDeviations = this.profile.sarDeviations.filter(s => s !== item);
  }

  showZipFileDialog(item, list) {
    this.editedZipFile = item;
    this.editedZipFileList = list;
    this.zipFileFormGroup.setValue({
      title: item.title || '',
      fileName: item.fileName || '',
      kind: item.kind
    });
    this.zipFileDialogVisible = true;
  }

  saveZipFile() {
    if (!this.zipFileFormGroup.valid) {
      Object.keys(this.zipFileFormGroup.controls).forEach(field => {
        const control = this.zipFileFormGroup.get(field);
        control.markAsTouched({onlySelf: true});
      });
      return;
    }
    const data = {...this.editedZipFile, ...this.zipFileFormGroup.value};
    const pos = this.editedZipFileList.indexOf(this.editedZipFile);
    if (pos >= 0) {
      this.editedZipFileList[pos] = {...this.editedZipFile, ...data};
    } else {
      this.editedZipFileList.push({...data});
    }
    this.zipFileDialogVisible = false;
    this.editedZipFile = null;
  }

  public AddZipFile(event) {

    let attachments = event.target.files;
    if (attachments.length > 0) {
      let file: File = attachments[0];
      this.sarProfileService.postZipFile(file, LoginHelper.getUserId())
        .subscribe((res) => {

            this.editedZipFile.sarZipFileAttachment = res;
            this.editedZipFile.sarZipFileAttachmentId = res.sarZipFileAttachmentId;
            this.editedZipFile.fileName = res.name;
            this.editedZipFile.internalName = res.internalName;
            this.zipFileFormGroup.setValue({...this.zipFileFormGroup.value, fileName: res.name});

          });
    }
  }

  deleteZipFile(item, list) {
    list.splice(list.indexOf(item, 1));
  }


  showScanSummaryDialog(item = <any>{}) {
    this.editedScanSummary = item;
    this.scanSummaryFormGroup.setValue({
      identifier: item.identifier || '',
      componentDescription: item.componentDescription || '',
      rationale: item.rationale || '',
      metodologyDescription: item.metodologyDescription || '',
    });
    this.scanSummaryDialogVisible = true;
  }

  saveScanSummary() {
    if (!this.scanSummaryFormGroup.valid) {
      Object.keys(this.scanSummaryFormGroup.controls).forEach(field => {
        const control = this.scanSummaryFormGroup.get(field);
        control.markAsTouched({onlySelf: true});
      });
      return;
    }
    const data = this.scanSummaryFormGroup.value;
    const pos = this.profile.sarScanSummaries.indexOf(this.editedScanSummary);
    if (pos >= 0) {
      this.profile.sarScanSummaries[pos] = {...this.editedScanSummary, ...data};
    } else {
      this.profile.sarScanSummaries.push({...data});
    }
    this.scanSummaryDialogVisible = false;
    this.editedScanSummary = null;
  }

  deleteScanSummary(item) {
    this.profile.sarScanSummaries = this.profile.sarScanSummaries.filter(s => s !== item);
  }


  showAuxiliaryDocumentDialog(item = <any>{}) {
    this.editedAuxiliaryDocument = item;
    this.auxiliaryDocumentFormGroup.setValue({
      title: item.title || '',
      version: item.version || '',
      fileName: item.fileName || ''
    });
    this.auxiliaryDocumentDialogVisible = true;
  }

  saveAuxiliaryDocument() {
    if (!this.auxiliaryDocumentFormGroup.valid) {
      Object.keys(this.auxiliaryDocumentFormGroup.controls).forEach(field => {
        const control = this.auxiliaryDocumentFormGroup.get(field);
        control.markAsTouched({onlySelf: true});
      });
      return;
    }
    const data = this.auxiliaryDocumentFormGroup.value;
    const pos = this.profile.sarAuxiliaryDocuments.indexOf(this.editedAuxiliaryDocument);
    if (pos >= 0) {
      this.profile.sarAuxiliaryDocuments[pos] = {...this.editedAuxiliaryDocument, ...data};
    } else {
      this.profile.sarAuxiliaryDocuments.push({...data});
    }
    this.auxiliaryDocumentDialogVisible = false;
    this.editedAuxiliaryDocument = null;
  }

  public AddAuxiliaryFile(event) {

    let attachments = event.target.files;
    if (attachments.length > 0) {
      let file: File = attachments[0];
      this.sarProfileService.postZipFile(file, LoginHelper.getUserId())
        .subscribe((res) => {

            this.editedAuxiliaryDocument.sarZipFileAttachment = res;
            this.editedAuxiliaryDocument.sarZipFileAttachmentId = res.sarZipFileAttachmentId;
            this.editedAuxiliaryDocument.fileName = res.name;
            this.editedAuxiliaryDocument.internalName = res.internalName;
            this.auxiliaryDocumentFormGroup.setValue({...this.auxiliaryDocumentFormGroup.value, fileName: res.name});

          });
    }
  }

  deleteAuxiliaryDocument(item) {
    this.profile.sarAuxiliaryDocuments = this.profile.sarAuxiliaryDocuments.filter(s => s !== item);
  }

  updateRowGroupMetaData(data) {
    this.rowGroupMetadata = {};
    if (data) {

      for (let i = 0; i < data.length; i++) {
        let rowData = data[i];
        let brand = rowData.controlItem.category;
        if (i == 0) {
          this.rowGroupMetadata[brand] = {index: 0, size: 1};
        } else {
          let previousRowData = data[i - 1];
          let previousRowGroup = previousRowData.controlItem.category;
          if (brand === previousRowGroup)
            this.rowGroupMetadata[brand].size++;
          else
            this.rowGroupMetadata[brand] = {index: i, size: 1};
        }
      }
    }
  }


  protected readonly encodeURIComponent = encodeURIComponent;
}
